import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';

import { deleteFunction } from '../../functions/actions';
import { deleteHandoff } from '../../handoff/actions';
import {
  addLanguage,
  deleteLanguage,
  updateDefaultLanguage,
} from '../../languages/actions';
import { deleteTemplate } from '../../sms/actions';
import { deleteAttribute } from '../../variant/actions';
import { updateLanguageBehavior } from '../actions';
import { languageBehaviorReferencesAdapter } from '../adapters';
import { DEFAULT_LANGUAGE_BEHAVIOR } from '../constants';
import type { LanguageBehavior } from '../types';

export const languageBehaviorAdapter = createEntityAdapter<
  LanguageBehavior,
  string
>({
  selectId: languageBehavior => languageBehavior.languageCode,
});

const initialState = languageBehaviorAdapter.getInitialState();

const slice = createSlice({
  name: 'languageBehavior',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateLanguageBehavior, (state, action) => {
      // We should always find the language behavior by language code
      const existingLanguageBehavior =
        state.entities[action.payload.languageCode];

      if (existingLanguageBehavior) {
        // Handle references separately to avoid type conflicts
        const {
          references,
          languageCode: _ignored,
          ...otherPayload
        } = action.payload;
        assignDefinedFields(existingLanguageBehavior, otherPayload);

        if (references) {
          assignDefinedFields(existingLanguageBehavior.references, references);
        }

        assignMetadata(action, existingLanguageBehavior);
        languageBehaviorAdapter.updateOne(state, {
          id: action.payload.languageCode,
          changes: existingLanguageBehavior,
        });
      } else {
        const newLanguageBehavior: LanguageBehavior = {
          ...DEFAULT_LANGUAGE_BEHAVIOR,
          behaviorPrompt: '',
          references: languageBehaviorReferencesAdapter.createInitialState(),
          languageCode: action.payload.languageCode,
        };
        assignDefinedFields(newLanguageBehavior, action.payload);
        assignMetadata(action, newLanguageBehavior);
        languageBehaviorAdapter.addOne(state, newLanguageBehavior);
      }
    });

    builder.addCase(addLanguage, (state, action) => {
      const languageCode = action.payload.code;
      if (state.entities[languageCode] === undefined) {
        const newLanguageBehavior: LanguageBehavior = {
          ...DEFAULT_LANGUAGE_BEHAVIOR,
          behaviorPrompt: '',
          references: languageBehaviorReferencesAdapter.createInitialState(),
          languageCode,
        };
        assignMetadata(action, newLanguageBehavior);

        languageBehaviorAdapter.addOne(state, newLanguageBehavior);
      }
    });

    builder.addCase(updateDefaultLanguage, (state, action) => {
      const languageCode = action.payload.languageCode;
      if (state.entities[languageCode] === undefined) {
        const newLanguageBehavior: LanguageBehavior = {
          ...DEFAULT_LANGUAGE_BEHAVIOR,
          behaviorPrompt: '',

          languageCode,
          references: languageBehaviorReferencesAdapter.createInitialState(),
        };

        assignMetadata(action, newLanguageBehavior);

        languageBehaviorAdapter.addOne(state, newLanguageBehavior);
      }
    });

    // Update references upon deleting a function
    builder.addCase(deleteFunction, (state, action) => {
      const { id: functionId } = action.payload;
      const languageBehaviors = Object.values(state.entities);
      languageBehaviors.forEach(languageBehavior => {
        if (languageBehavior.references.globalFunctions[functionId]) {
          languageBehaviorReferencesAdapter.removeReference(
            state,
            languageBehavior.languageCode,
            'globalFunctions',
            functionId,
          );
        }
      });
    });

    // Update references upon deleting a handoff
    builder.addCase(deleteHandoff, (state, action) => {
      const { id: handoffId } = action.payload;
      const languageBehaviors = Object.values(state.entities);
      languageBehaviors.forEach(languageBehavior => {
        if (languageBehavior.references.handoff[handoffId]) {
          languageBehaviorReferencesAdapter.removeReference(
            state,
            languageBehavior.languageCode,
            'handoff',
            handoffId,
          );
        }
      });
    });

    // Update references upon deleting an SMS template
    builder.addCase(deleteTemplate, (state, action) => {
      const { id: smsId } = action.payload;
      const languageBehaviors = Object.values(state.entities);
      languageBehaviors.forEach(languageBehavior => {
        if (languageBehavior.references.sms[smsId]) {
          languageBehaviorReferencesAdapter.removeReference(
            state,
            languageBehavior.languageCode,
            'sms',
            smsId,
          );
        }
      });
    });

    // Update references upon deleting an attribute
    builder.addCase(deleteAttribute, (state, action) => {
      const { id: attributeId } = action.payload;
      const languageBehaviors = Object.values(state.entities);
      languageBehaviors.forEach(languageBehavior => {
        if (languageBehavior.references.attributes[attributeId]) {
          languageBehaviorReferencesAdapter.removeReference(
            state,
            languageBehavior.languageCode,
            'attributes',
            attributeId,
          );
        }
      });
    });

    // update language behavior when language is removed
    builder.addCase(deleteLanguage, (state, action) => {
      const { code: languageCode } = action.payload;
      if (state.entities[languageCode] !== undefined) {
        languageBehaviorAdapter.removeOne(state, languageCode);
      }
    });
  },
});

export default slice.reducer;
